import React, { useEffect, useState, createContext, useContext } from 'react';
import { PersonalizationPreferences, QuestionnaireState } from './PersonalizationTypes';
import { getPreferences, savePreferences, getDefaultPreferences, hasSeenQuestionnaire, markQuestionnaireAsSeen, resetQuestionnaireStatus } from './QuestionnaireStorage';
interface QuestionnaireContextType {
  state: QuestionnaireState;
  showQuestionnaire: boolean;
  openQuestionnaire: () => void;
  closeQuestionnaire: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  updatePreference: <K extends keyof PersonalizationPreferences>(key: K, value: PersonalizationPreferences[K]) => void;
  completeQuestionnaire: () => void;
  skipQuestionnaire: () => void;
  resetQuestionnaire: () => void;
}
const initialState: QuestionnaireState = {
  isComplete: false,
  currentStep: 1,
  totalSteps: 20,
  preferences: getDefaultPreferences(),
  hasSeenQuestionnaire: false
};
const QuestionnaireContext = createContext<QuestionnaireContextType | undefined>(undefined);
export const QuestionnaireProvider: React.FC<{
  children: ReactNode;
}> = ({
  children
}) => {
  const [state, setState] = useState<QuestionnaireState>(initialState);
  const [showQuestionnaire, setShowQuestionnaire] = useState<boolean>(false);
  // Initialize state from storage on component mount
  useEffect(() => {
    const preferences = getPreferences();
    const seen = hasSeenQuestionnaire();
    setState(prevState => ({
      ...prevState,
      preferences,
      hasSeenQuestionnaire: seen,
      isComplete: seen
    }));
    // Show questionnaire if it hasn't been seen before
    if (!seen) {
      setShowQuestionnaire(true);
    }
  }, []);
  const openQuestionnaire = () => setShowQuestionnaire(true);
  const closeQuestionnaire = () => setShowQuestionnaire(false);
  const nextStep = () => {
    setState(prevState => {
      const nextStep = prevState.currentStep + 1;
      return {
        ...prevState,
        currentStep: nextStep <= prevState.totalSteps ? nextStep : prevState.currentStep
      };
    });
  };
  const prevStep = () => {
    setState(prevState => {
      const prevStep = prevState.currentStep - 1;
      return {
        ...prevState,
        currentStep: prevStep >= 1 ? prevStep : prevState.currentStep
      };
    });
  };
  const goToStep = (step: number) => {
    setState(prevState => ({
      ...prevState,
      currentStep: step >= 1 && step <= prevState.totalSteps ? step : prevState.currentStep
    }));
  };
  const updatePreference = <K extends keyof PersonalizationPreferences,>(key: K, value: PersonalizationPreferences[K]) => {
    setState(prevState => {
      const updatedPreferences = {
        ...prevState.preferences,
        [key]: value
      };
      // Save to storage with each update
      savePreferences(updatedPreferences);
      return {
        ...prevState,
        preferences: updatedPreferences
      };
    });
  };
  const completeQuestionnaire = () => {
    markQuestionnaireAsSeen();
    setState(prevState => ({
      ...prevState,
      isComplete: true,
      hasSeenQuestionnaire: true
    }));
    closeQuestionnaire();
  };
  const skipQuestionnaire = () => {
    markQuestionnaireAsSeen();
    setState(prevState => ({
      ...prevState,
      isComplete: true,
      hasSeenQuestionnaire: true
    }));
    closeQuestionnaire();
  };
  const resetQuestionnaire = () => {
    resetQuestionnaireStatus();
    setState({
      ...initialState,
      preferences: getDefaultPreferences(),
      hasSeenQuestionnaire: false
    });
    openQuestionnaire();
  };
  return <QuestionnaireContext.Provider value={{
    state,
    showQuestionnaire,
    openQuestionnaire,
    closeQuestionnaire,
    nextStep,
    prevStep,
    goToStep,
    updatePreference,
    completeQuestionnaire,
    skipQuestionnaire,
    resetQuestionnaire
  }} data-id="element-15274">
      {children}
    </QuestionnaireContext.Provider>;
};
export const useQuestionnaire = (): QuestionnaireContextType => {
  const context = useContext(QuestionnaireContext);
  if (context === undefined) {
    throw new Error('useQuestionnaire must be used within a QuestionnaireProvider');
  }
  return context;
};